import { BaseTable } from '@old/features/ui';
import { GridLegacy as Grid, IconButton, Chip, Stack, Box, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { formatDate, DateMonth, formatDateTime } from '@old/utils/helper';

import checkIcon from '@old/assets/svg/check.svg';

import siloIdIcon from '@old/assets/svg/SiloBagPage/silo-id.svg';
import SiloIdIconDisabled from '@old/assets/svg/SiloBagPage/silo-id-disabled.svg';

import siloMetrix from '@old/assets/svg/SiloBagPage/silo-metrix-active.svg';
import siloMetrixDisabled from '@old/assets/svg/SiloBagPage/silo-metrix-disabled.svg';

import siloSat from '@old/assets/svg/SiloBagPage/silo-view-active.svg';
import siloSatDisabled from '@old/assets/svg/SiloBagPage/silo-view-disabled.svg';

import { PATH } from '@old/data/core/paths';
import { useNavigate } from 'react-router';
import { useService } from '@old/hooks/use-service';
import { useEffectOnUpdate } from '@old/hooks/core';
import ChevronRightIcon from '@old/assets/svg/core/chevron-right-blue.svg';
import { SilobagShareModal } from '../silobag-share/silobag-share';
import { SilobagTagModal } from '../silobag-tag/silobag-tag-modal';
import { sharePermissionListService } from '@old/services/domain/base/silobag/share/share-list';
import { useToggle } from '@old/hooks/core';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import { Dispatch, SetStateAction, useState, useEffect, useRef, useMemo } from 'react';
import i18n from '../../../libs/language';
import { ToastAction } from '@old/ui/toast/toast-action';
import viewIcon from '@old/assets/svg/core/eye-hollow-blue.svg';
import mapIcon from '@old/assets/svg/core/location-pin-hollow-blue.svg';
import editIcon from '@old/assets/svg/core/edit-square-hollow-blue.svg';
import editDisabledIcon from '@old/assets/svg/core/edit-square-hollow-disabled.svg';
import downloadPdfIcon from '@old/assets/svg/core/download-hollow-blue.svg';
import downloadDisabledPdfIcon from '@old/assets/svg/core/dowload-hollow-disabled.svg';
import shareIcon from '@old/assets/svg/core/share-hollow-blue.svg';
import shareDisabledIcon from '@old/assets/svg/share-disabled.svg';
import mapIconDisabled from '@old/assets/svg/core/location-pin-hollow-disabled.svg';
import { silobagCMCService } from '@old/services/domain/base/silobag/file/cmc';
import { listSilobagService } from '@old/services/domain/base/silobag/base/silobag-list';
import warningIcon from '@old/assets/svg/warning.svg';
import { useGlobalStore } from '@old/global-store/use-store';
import { useShallow } from 'zustand/react/shallow';

type SummarySilobagList = Awaited<ReturnType<typeof listSilobagService>>['summary']['data'];
type SummarySilobag = SummarySilobagList[0];
type SilobagListProps = {
  silobagList?: SummarySilobagList;
  isLoading: boolean;
  isActiveList?: boolean;
  rowCount: number;
  paginationModel: GridPaginationModel;
  setPaginationModel: (fn: (model: GridPaginationModel) => GridPaginationModel) => void;
  //sortingModel: GridSortModel;
  setSortingModel: Dispatch<SetStateAction<string>>;
  targetSilobagDataSelection: SummarySilobagList;
  onSilobagDataSelectionChange: (silobags: SummarySilobagList) => void;
  setSiloListAll: (val: SummarySilobagList) => void;
  onOfferSelectAllSilobagsChange: (offer?: boolean | null) => void;
  onTagsAssigned: () => void;
};

export function SilobagListTablet({
  silobagList,
  isLoading,
  isActiveList,
  rowCount,
  paginationModel,
  setPaginationModel,
  setSortingModel,
  targetSilobagDataSelection,
  onSilobagDataSelectionChange,
  setSiloListAll,
  onOfferSelectAllSilobagsChange,
  onTagsAssigned,
}: SilobagListProps) {
  const { setFiltersInconsistencies, filtersInconsistencies, preferedLanguageId } = useGlobalStore(
    useShallow((s) => s)
  );
  const navigate = useNavigate();
  // Stabilize rows reference to avoid re-renders/loops when list is not yet loaded
  const rows = useMemo(() => silobagList ?? ([] as SummarySilobagList), [silobagList]);
  const siloListId = useMemo(() => rows.map((row) => row.id), [rows]);

  const [modalShare, setModalShare] = useToggle(false);
  const [modalTag, setModalTag] = useToggle(false);
  const [siloName, setSiloName] = useState('');
  const [siloIdShare, setSiloIdShare] = useState('');
  const [siloIdTag, setSiloIdTag] = useState('');
  const [siloNameTag, setSiloNameTag] = useState('');
  const [validateSB, setValidateSB] = useState(false);
  const [toastState, setToastState] = useToggle(false);
  const [countryside, setCountryside] = useState('');

  // VERIFICAR SI LA SB FUE COMPARTIDA CON EXITO
  const [silobagShared, setSilobagShared] = useState<{
    message: string;
    state: boolean;
    type: 'success' | 'error';
  }>({ message: '', state: false, type: 'success' });

  useEffectOnUpdate(() => {
    setToastState(silobagShared.state);
  }, [silobagShared]);

  const { trigger: getPdf, summary: data, isLoading: isLoadingPdf } = useService(silobagCMCService);

  useEffectOnUpdate(() => {
    if (isLoadingPdf) {
      document.body.style.cursor = 'wait';
    } else if (!isLoadingPdf && data) {
      document.body.style.cursor = 'auto';
      const link = document.createElement('a');
      link.href = data.file_path;
      link.download = `${data.file_path}`;
      link.click();
    }
  }, [isLoadingPdf]);

  const {
    trigger: getSiloPermission,
    summary: dataSiloPermission,
    isLoading: isLoadingSiloPermission,
  } = useService(sharePermissionListService);

  const handleModalShare = (
    siloId: string,
    siloName: string,
    isValidateSB: boolean,
    countrysideName: string
  ) => {
    getSiloPermission({ query: siloId });
    setSiloName(siloName);
    setSiloIdShare(siloId);
    setValidateSB(!isValidateSB);
    setModalShare(true);
    setCountryside(countrysideName);
  };

  const pathSilobags = true;
  const columns: GridColDef<SummarySilobag>[] = [
    {
      field: 'name',
      display: 'flex',
      headerName: 'Silo',
      flex: 1.5,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const siloIdNavigate = params.row.id;

        // const orgFakeTags: Partial<(typeof params.row.groupedTags.organization)[0]>[] = [
        //   { label: 'Listo para asegurar', bg_color: '#22C55E', text_color: '#fff' },
        //   { label: 'Bolsa vieja', bg_color: '#3F3F46', text_color: '#fff' },
        //   { label: 'Prioridad de Retiro', bg_color: '#F97316', text_color: '#fff' },
        //   { label: 'Stock crítico', bg_color: '#EF4444', text_color: '#fff' },
        // ];

        // const systemFakeTags: Partial<(typeof params.row.groupedTags.system)[0]>[] = [
        //   { label: 'iW', bg_color: '#7C3AED', text_color: '#fff' },
        //   { label: 'SS', bg_color: '#6D28D9', text_color: '#fff' },
        // ];

        const orgTags = params.row.groupedTags?.find((g) => g.scope === 'organization')?.tags ?? [];
        const systemTags = params.row.groupedTags?.find((g) => g.scope === 'system')?.tags ?? [];

        const effectiveOrgTags = orgTags; //?? orgFakeTags;
        const effectiveSystemTags = systemTags; //?? systemFakeTags;

        const visibleTags = effectiveOrgTags.slice(0, 3);
        const hiddenTags = effectiveOrgTags.slice(3);

        return (
          <Stack spacing={0.5} justifyContent="space-between" sx={{ width: '100%' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ overflow: 'hidden', width: '100%' }}
            >
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  maxWidth: '100%',
                  minWidth: 0,
                  textDecoration: 'none',
                  color: 'black',
                  cursor: 'pointer',
                  fontSize: '.85rem',
                  '&:hover': { color: '#22a7f0' },
                  overflow: 'hidden',
                }}
                onClick={() =>
                  navigate(`${PATH.SILOBAG}/${siloIdNavigate}`, { state: { siloListId, pathSilobags } })
                }
              >
                <Tooltip title={params.value} placement="top" arrow>
                  <Box
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {params.value}
                  </Box>
                </Tooltip>
                <img src={ChevronRightIcon} style={{ width: 8, height: 8, marginLeft: 4, flexShrink: 0 }} />
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                sx={{ flexShrink: 1, minWidth: 0, ml: 1, flexWrap: 'nowrap', overflow: 'hidden' }}
              >
                {effectiveSystemTags.map((chip, i) => {
                  const translation = chip.translations.find((p) => p.language == preferedLanguageId)?.value;
                  const translationTooltip = chip.translations.find(
                    (p) => p.language == preferedLanguageId
                  )?.tooltip;

                  return (
                    <Tooltip key={i} title={translationTooltip} placement="top" arrow>
                      <Chip
                        icon={
                          chip.image_url ? (
                            <img
                              src={chip.image_url}
                              alt=""
                              style={{ width: 14, height: 14, marginLeft: 4, objectFit: 'contain' }}
                            />
                          ) : undefined
                        }
                        label={translation}
                        size="small"
                        sx={{
                          backgroundColor: chip.bg_color,
                          color: chip.text_color,
                          fontSize: '10px',
                          fontWeight: 600,
                          height: '18px',
                          borderRadius: '9px',
                          '& .MuiChip-label': {
                            px: '6px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              sx={{ flexWrap: 'nowrap', overflow: 'hidden' }}
            >
              {visibleTags.map((chip, i) => {
                const translation = chip.translations.find((p) => p.language == preferedLanguageId)?.value;
                return (
                  <Tooltip key={i} title={translation} placement="top" arrow>
                    <Chip
                      label={translation}
                      size="small"
                      sx={{
                        backgroundColor: chip.bg_color,
                        color: chip.text_color,
                        fontSize: '10px',
                        fontWeight: 500,
                        height: '18px',
                        borderRadius: '4px',
                        maxWidth: '54px',
                        '& .MuiChip-label': {
                          px: '6px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
              {hiddenTags.length > 0 ? (
                <Tooltip
                  title={
                    <Stack gap={0.5}>
                      {hiddenTags.map((chip, i) => (
                        <Chip
                          key={i}
                          label={chip.translations.find((p) => p.language == preferedLanguageId)?.value}
                          size="small"
                          sx={{
                            backgroundColor: chip.bg_color,
                            color: chip.text_color,
                            fontSize: '10px',
                            fontWeight: 500,
                            height: '18px',
                            borderRadius: '4px',
                            '& .MuiChip-label': { px: '6px' },
                          }}
                        />
                      ))}
                    </Stack>
                  }
                  arrow
                  placement="top"
                >
                  <Chip
                    label={`+${hiddenTags.length}`}
                    size="small"
                    sx={{
                      backgroundColor: '#E4E4E7',
                      color: '#3F3F46',
                      fontSize: '10px',
                      fontWeight: 600,
                      height: '18px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      '& .MuiChip-label': { px: '6px' },
                    }}
                  />
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: 'silobag_id',
      display: 'flex',
      headerName: 'Id',
      flex: 0.8,
      minWidth: 80,
      sortable: false,
      resizable: false,
      renderCell: (params) => {
        const siloId = params.row.id;
        return <Box style={{ fontSize: '.85rem' }}>{siloId}</Box>;
      },
    },
    {
      field: 'services',
      display: 'flex',
      headerName: 'Servicios',
      flex: 0.8,
      minWidth: 90,
      sortable: false,
      resizable: false,
      renderCell: (params) => {
        const siloId = params.row.silobag_data_statuses.id;
        const siloSatService = params.row.v_silobags_services_statuses?.sat_view_active;
        const siloMetrixService = params.row.v_silobags_services_statuses?.silo_metrix_active;
        return (
          <Stack direction="row" alignItems="center">
            <img
              src={siloId === 2 ? siloIdIcon : SiloIdIconDisabled}
              alt="siloId"
              style={{ marginRight: '5px' }}
            />
            <img
              src={siloSatService === true ? siloSat : siloSatDisabled}
              alt="siloSat"
              style={{ marginRight: '5px' }}
            />
            <img src={siloMetrixService === true ? siloMetrix : siloMetrixDisabled} alt="siloMetrix" />
          </Stack>
        );
      },
    },
    {
      field: 'silobag_species_types',
      display: 'flex',
      headerName: i18n.t('app.silobag_data.species'),
      sortable: true,
      flex: 1.2,
      minWidth: 135,
      resizable: false,
      valueGetter: (_, row) => {
        const translation = row.silobag_species_types.silobag_species_types_translations.find(
          (t) => t.language_id === (i18n.locale === 'es' ? 1 : 2)
        );
        return translation ? translation.description : row.silobag_species_types.name;
      },
      renderCell: (params) => {
        const imageSpecie = params.row.silobag_species_types.image_url;
        return (
          <Stack>
            <Stack direction="row" alignItems="center">
              <img
                src={imageSpecie}
                alt="imagen de icono"
                style={{ width: 18, height: 18, marginRight: '5px' }}
              />
              <Box style={{ fontSize: '.85rem' }}>{params.value}</Box>
            </Stack>
            <Box style={{ fontSize: '.85rem' }}>{params.row?.variety}</Box>
          </Stack>
        );
      },
    },
    {
      field: 'silobag_product_types',
      display: 'flex',
      headerName: i18n.t('app.silobag_data.product'),
      flex: 1,
      resizable: false,
      minWidth: 100,
      valueGetter: (_, row) => {
        // console.log('CHECK (product):', params, row2, a, b);
        const translation = row.silobag_product_types.silobag_product_types_translations.find(
          (t) => t.language_id === (i18n.locale === 'es' ? 1 : 2)
        );
        return translation ? translation.description : row.silobag_product_types.name;
        // const v = (
        //   params as unknown as { value?: { silobag_product_types_translations?: { description?: string }[] } }
        // )?.value;
        // return v?.silobag_product_types_translations?.[0]?.description ?? '';
      },
      renderCell: (params) => <Box style={{ fontSize: '0.85rem' }}>{params.value}</Box>,
    },
    {
      field: isActiveList ? 'created_at' : 'closing_date',
      display: 'flex',
      minWidth: 100,
      headerName: isActiveList
        ? i18n.t('app.silobag_data.register_date')
        : i18n.t('app.silobag_data.close_date'),
      flex: 1,
      resizable: false,
      renderCell: (params) => {
        const closeData = params.row?.v_silobag_closing_dates?.closing_date.toString();
        return (
          <Box style={{ fontSize: '0.85rem' }}>
            <Box>{isActiveList ? formatDateTime(params.value, 4) : formatDateTime(closeData, 4)}</Box>
            {closeData !== null ? (
              <Box style={{ fontSize: '10px' }}>
                {isActiveList ? '' : `Alta ${formatDate(params?.row?.created_at)}`}
              </Box>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'bagging_date',
      display: 'flex',
      headerName: i18n.t('app.silobag_data.bagging_date'),
      minWidth: 120,
      resizable: false,
      flex: 1,
      renderCell: (params) => {
        const date = params.value ? formatDate(params.value) : '―';
        const dateWithMonth = date !== '―' ? DateMonth(date) : '―';
        const harverstDate = params.row.silobag_harvests?.name;
        return (
          <Stack>
            <Box>{dateWithMonth}</Box>
            <Box style={{ fontSize: '12px' }}>
              <span style={{ color: '#B7B7B7' }}>{i18n.t('app.silobag_data.harvest')}</span> {harverstDate}
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'initial_humidity',
      display: 'flex',
      headerName: i18n.t('app.silobag_data.initial_humidity'),
      align: 'center',
      headerAlign: 'left',
      type: 'number',
      flex: 1,
      resizable: false,
      minWidth: 150,
      renderCell: (params) => {
        const valueToDisplay = params.value != null ? params.value + ' %' : '―';
        return <span style={{ fontSize: '0.85rem' }}>{valueToDisplay}</span>;
      },
    },
    {
      field: 'remaining_stock',
      display: 'flex',
      minWidth: 120,
      headerAlign: 'left',
      headerName: i18n.t('app.silobag_data.remaining_stock'),
      type: 'number',
      flex: 1.2,
      align: 'left',
      resizable: false,
      renderCell: (params) => {
        const active = params.row.active;
        const partially_extracted = params.row.partially_extracted;
        let chipLabel = '';
        let chipBgColor = '';
        let chipTextColor = '';
        if ((active === true && partially_extracted === false) || partially_extracted === null) {
          chipLabel = i18n.t('app.active');
          chipBgColor = '#73BD0033';
          chipTextColor = '#6BAA00';
        } else if (active == true && partially_extracted === true) {
          chipLabel = i18n.t('app.partially_extracted');
          chipBgColor = '#FD7E1433';
          chipTextColor = '#FD7E14';
        } else if (active === false) {
          chipLabel = i18n.t('app.closed');
          chipBgColor = '#A7A7A733';
          chipTextColor = '#7A7A7A';
        }
        return (
          <Stack>
            <Box style={{ fontSize: '0.85rem' }}>
              {i18n.numberToDelimited(parseFloat(params.value || 0), {
                delimiter: i18n.t('config.delimiter'),
              })}{' '}
              kg
            </Box>
            <Box>
              <Chip
                label={chipLabel}
                size="small"
                sx={{
                  backgroundColor: chipBgColor,
                  color: chipTextColor,
                  fontSize: '10px',
                  height: '17px',
                  borderRadius: '4px',
                }}
              />
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'total_extracted_weight',
      display: 'flex',
      headerName: i18n.t('app.silobag_data.total_extracted'),
      type: 'number',
      headerAlign: 'left',
      align: 'center',
      width: 120,
      resizable: false,
      renderCell: (params) => (
        <span style={{ fontSize: '0.85rem' }}>
          {i18n.numberToDelimited(params.value, { delimiter: i18n.t('config.delimiter') })} Kg
        </span>
      ),
    },
    {
      field: 'organizations',
      display: 'flex',
      headerName: i18n.t('app.organization'),
      flex: 1,
      minWidth: 115,
      resizable: false,
      sortable: false,
      valueGetter: (value) => (value as unknown as { name?: string })?.name ?? '',
      renderCell: (params) => {
        const row = (params as unknown as { row?: SummarySilobag })?.row;
        // return (<span style={{ fontSize: '0.85rem' }}>{params.value}</span>)
        return (
          <Stack style={{ fontSize: '0.85rem' }}>
            <Tooltip title={params.value} placement="top" arrow>
              <Box
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value}
              </Box>
            </Tooltip>
            <Typography fontSize={12} color="textSecondary">
              <Tooltip title={row?.companies?.name ?? ''} placement="bottom" arrow>
                <Box
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {row?.companies?.name ?? ''}
                </Box>
              </Tooltip>
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'lots',
      display: 'flex',
      headerName: i18n.t('app.countryside'),
      flex: 1.3,
      resizable: false,
      minWidth: 200,
      valueGetter: (value) =>
        (value as unknown as { countrysides: { name?: string } })?.countrysides?.name ?? '',
      renderCell: (params) => {
        const row = (params as unknown as { row?: SummarySilobag })?.row;
        return (
          <Stack style={{ fontSize: '0.85rem' }}>
            <Tooltip title={params.value} placement="top" arrow>
              <Box
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value}
              </Box>
            </Tooltip>
            <Typography fontSize={12} color="textSecondary">
              <Tooltip title={row?.lots?.name ?? ''} placement="bottom" arrow>
                <Box
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {row?.lots?.name ?? ''}
                </Box>
              </Tooltip>
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'hex',
      display: 'flex',
      headerName: i18n.t('app.alerts'),
      flex: 0.8,
      sortable: false,
      resizable: false,
      minWidth: 74,
      renderCell: (params) => {
        const hexBgColor = params.value;
        const short_name_alert = params.row.short_name_last_alert;
        const severity = params.row.severity;

        return (
          <Tooltip title={i18n.t('app.tooltip_sb_optimo')} disableHoverListener={severity !== 0}>
            <Box style={{ width: '100%' }}>
              <Chip
                label={
                  short_name_alert == null || severity == 0 ? (
                    <img src={checkIcon} alt="Check Icon" />
                  ) : (
                    short_name_alert
                  )
                }
                sx={{
                  backgroundColor: hexBgColor,
                  color: severity === 1 ? '#000' : '#fff',
                  width: 74,
                  height: 26,
                  fontSize: '10px',
                  fontWeight: 500,
                  borderRadius: '8px',
                }}
              />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'id',
      display: 'flex',
      headerName: i18n.t('app.actions'),
      flex: 2.2,
      sortable: false,
      resizable: false,
      minWidth: 200,
      renderCell: (params) => {
        const siloId = params.value;
        const siloName = params.row.name;
        const writePermission = params.row.write_permission;
        const fullPermission = params.row.full_permission;
        const isGuest: boolean = params.row.is_guest;
        const isSharedExternally: boolean = params.row.is_shared_externally ?? false;
        const countrysideName = params.row.lots.countrysides.name;
        const isValidateSB = params?.row?.silobag_data_statuses.id === 2 ? true : false;
        const isCloseSB = params?.row?.active;
        const isValidMap = isCloseSB && isValidateSB;
        const haveDiscrepany = params.row.disc_discrepancies_results.length > 0;
        return (
          <Stack direction={'row'} gap={0.3}>
            <Tooltip title={haveDiscrepany ? i18n.t('app.inconsistencies_page.view_inconsistencies') : ''}>
              <span>
                <IconButton
                  sx={{
                    color: '#22A7F0',
                    width: 24,
                    height: 24,
                    padding: '4px',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#22a7f02a',
                    },
                  }}
                  disabled={!haveDiscrepany}
                  onClick={() => {
                    setFiltersInconsistencies({
                      ...filtersInconsistencies,
                      silobagId: siloId,
                      // silobagName: siloName,
                    });
                    navigate(`${PATH.INCONSISTENCIES}`, { state: { filteredId: true } });
                  }}
                >
                  <img
                    src={haveDiscrepany ? warningIcon : checkIcon}
                    alt="eye"
                    style={{ width: 16, height: 16 }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={i18n.t('app.see_details')}>
              <span
                onClick={() => navigate(`${PATH.SILOBAG}/${siloId}`, { state: { siloListId, pathSilobags } })}
              >
                <IconButton
                  sx={{
                    color: '#22A7F0',
                    width: 24,
                    height: 24,
                    padding: '4px',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#22a7f02a',
                    },
                  }}
                >
                  <img src={viewIcon} alt="eye" style={{ width: 16, height: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={i18n.t('app.dashboard_page.see_map')}>
              <span>
                <IconButton
                  sx={{
                    color: '#22A7F0',
                    width: 24,
                    height: 24,
                    padding: '4px',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#22a7f02a',
                    },
                  }}
                  disabled={!isValidMap}
                  onClick={() => navigate(`${PATH.MAP}/${params.id}`)}
                >
                  <img
                    src={isValidMap ? mapIcon : mapIconDisabled}
                    alt="map"
                    style={{ width: 16, height: 16 }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={i18n.t('app.edit_siloreal')}>
              <span>
                <IconButton
                  onClick={() => navigate(`${PATH.SILOBAG}/${siloId}/edit`, { state: { isGuest } })}
                  disabled={!writePermission}
                  sx={{
                    color: '#22A7F0',
                    width: 24,
                    height: 24,
                    padding: '4px',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#22a7f02a',
                    },
                  }}
                >
                  <img
                    src={writePermission && isActiveList ? editIcon : editDisabledIcon}
                    alt="edit"
                    style={{ width: 16, height: 16 }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={i18n.t('app.silobag_tags.tags')}>
              <span>
                <IconButton
                  disabled={isGuest}
                  onClick={() => {
                    setSiloIdTag(siloId);
                    setSiloNameTag(siloName);
                    setModalTag(true);
                  }}
                  sx={{
                    color: '#22A7F0',
                    width: 24,
                    height: 24,
                    padding: '4px',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#22a7f02a',
                    },
                  }}
                >
                  <SellOutlinedIcon sx={{ width: 16, height: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={i18n.t('app.download_certificate_silo')}>
              <span>
                <IconButton
                  onClick={() => getPdf({ query: { id: siloId, blockchain: true, silobagHistory: true } })}
                  disabled={!isValidateSB}
                  sx={{
                    color: '#22A7F0',
                    width: 24,
                    height: 24,
                    padding: '4px',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#22a7f02a',
                    },
                  }}
                >
                  <img
                    src={isValidateSB ? downloadPdfIcon : downloadDisabledPdfIcon}
                    alt="download"
                    style={{ width: 16, height: 16 }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                isSharedExternally
                  ? i18n.t('app.silobag_share.shared_externally')
                  : i18n.t('app.silobag_share.share_silobag')
              }
            >
              <span>
                <IconButton
                  onClick={() => handleModalShare(siloId, siloName, isValidateSB, countrysideName)}
                  disabled={!fullPermission}
                  sx={{
                    color: isSharedExternally ? '#FF8C00' : '#22A7F0',
                    width: 24,
                    height: 24,
                    padding: '4px',
                    borderRadius: '4px',
                    marginRight: '4px',
                    '&:hover': {
                      background: isSharedExternally ? '#ff8c002a' : '#22a7f02a',
                    },
                  }}
                >
                  <img
                    src={fullPermission ? shareIcon : shareDisabledIcon}
                    alt="share"
                    style={{
                      width: 16,
                      height: 16,
                      filter:
                        isSharedExternally && fullPermission
                          ? 'sepia(1) saturate(5) hue-rotate(-10deg) brightness(1.1)'
                          : undefined,
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  // Only update parent when the actual source list changes
  useEffect(() => {
    setSiloListAll(rows);
  }, [silobagList]);

  // const [trackingSelection, setTrackingSelection] = useState<GridRowSelectionModel>([]);
  // const [trackingPage, setTrackingPage] = useState(userPaginationModel.page);
  // const [trackingPageSize, setTrackingPageSize] = useState(userPaginationModel.pageSize);
  // const [trackPageChanged, setTrackPageChanged] = useState(false);
  // const [trackSortChanged, setTrackSortChanged] = useState(false);
  const trackSortChanges = useRef(false);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    /*
    [NEW SELECTION]
    - Trae solo SiloID, necesito todos los datos completos para agregarlo al SelectionModel
    - Puede traer silobolsas que ya estén en targetSilobagDataSelection
    - Los silobolsas que SÍ están en targetSilobagDataSelection pero que NO están en newSelection son silobolsas que fueron deseleccionados.
    [ROW]
    - Son los silobolsas que se ven en la página actual
    [TARGET SILOBAG DATA SELECTION]
    - Son todos los silobolsas seleccionados actualmente
    [MATERIAL-UI LOGIC]
    - Ve el selectionModel por página, es decir, si se cambia de una página con 3 selecciones a una con 0 (normal porque las páginas empiezan con todo deseleccionado) entonces se llama al handler del selectionModel 
    */

    // const addedSbData = newSelection.reduce<SummarySilobagList>((rec, id) => {
    //   const found = targetSilobagDataSelection.find((row) => row.id === id);
    //   if (found) return rec;
    //   const newData = rows.find((row) => row.id === id)!;
    //   return [...rec, newData];
    // }, []);
    // const removedIDs = targetSilobagDataSelection
    //   .filter((row) => !newSelection.includes(row.id))
    //   .map((row) => row.id);

    // const newDataSelected = targetSilobagDataSelection
    //   .filter((row) => row.silobag_data_statuses.id === filters.silobag_data_status_id)
    //   .filter((row) => !removedIDs.includes(row.id))
    //   .concat(addedSbData);
    const addedSilobags = rows.filter(
      (r) => newSelection.ids.has(r.id) && !targetSilobagDataSelection.find((ss) => ss.id === r.id)
    );
    const removedSilobags = rows.filter((r) => !newSelection.ids.has(r.id));

    const newDataSelected = targetSilobagDataSelection
      .concat(addedSilobags)
      .filter((ss) => !removedSilobags.find((r) => r.id === ss.id));
    const updateOffer = onOfferSelectAllSilobagsChange;
    newDataSelected.length >= 10 ? updateOffer(true) : updateOffer(false);

    onSilobagDataSelectionChange(newDataSelected);
  };

  return (
    <>
      {silobagShared.state ? (
        <ToastAction
          message={silobagShared.message}
          severity={silobagShared.type}
          openToast={toastState}
          setOpenToast={setToastState}
        />
      ) : null}
      <Grid item xs={12} style={{ marginTop: '20px' }}>
        <BaseTable
          loading={isLoading}
          rowCount={rowCount ?? 0}
          rowHeight={52}
          rows={rows}
          columns={columns}
          disableColumnResize={false}
          initialPageSize={50}
          initialState={{
            pinnedColumns: {
              right: ['hex', 'id'],
            },
          }}
          enableSelection
          // localeText={{
          //   footerRowSelected: () => {
          //     const silobagSelectedQtyInCurrentPage = rows.filter((r) =>
          //       targetSilobagDataSelection.find((s) => s.id === r.id)
          //     ).length;
          //     return `${silobagSelectedQtyInCurrentPage} silobags selected in this page.`;
          //   },
          // }}
          checkboxSelection
          rowSelectionModel={{
            type: 'include',
            ids: new Set(targetSilobagDataSelection.map((row) => row.id)),
          }}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onRowSelectionModelChange={handleSelectionChange}
          disableRowSelectionOnClick
          // Server comunication
          sortingMode="server"
          onSortModelChange={(sortModel) => {
            trackSortChanges.current = true;
            if (!sortModel.length) return '';

            const FIELD_MAPPING: { [key: string]: string } = {
              name: 'silobag_name',
              silobag_species_types: 'species_type_name',
              silobag_product_types: 'product_type_name',
              bagging_date: 'bagging_date',
              initial_humidity: 'initial_humidity',
              remaining_stock: 'remaining_stock',
              total_extracted_weight: 'total_extracted_weight',
              lots: 'countryside_name',
              created_at: 'created_at',
              closing_date: 'closing_date',
              //silobag_id: '',
              //organizations: '',
              //hex: '',
            };

            const sortedFields = sortModel.map((s) => FIELD_MAPPING[s.field] ?? '').filter(Boolean);
            const sortedOrders = sortModel.map((s) => s.sort);
            const sortByFieldQuery = 'orderBy=' + sortedFields.join(',');
            const sortByOrderQuery = 'orderByDirections=' + sortedOrders.join(',');

            setSortingModel(sortByFieldQuery + '&' + sortByOrderQuery);
          }}
          paginationMode="server"
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          selectFullRow
        />
        {modalTag && (
          <SilobagTagModal
            open={modalTag}
            setOpen={setModalTag}
            siloId={siloIdTag}
            siloName={siloNameTag}
            onTagsAssigned={onTagsAssigned}
          />
        )}
        {modalShare && (
          <SilobagShareModal
            siloName={siloName}
            siloId={siloIdShare}
            open={modalShare}
            setOpen={setModalShare}
            data={dataSiloPermission}
            getData={getSiloPermission}
            isLoadingData={isLoadingSiloPermission}
            setSilobagShared={setSilobagShared}
            hasInvalidStatus={validateSB}
            CountrysideName={countryside}
            silobagsData={[
              {
                id: siloIdShare,
                name: siloName,
                full_permission: rows.find((r) => r.id === siloIdShare)?.full_permission ?? false,
              },
            ]}
          />
        )}
      </Grid>
    </>
  );
}
